import { LitElement, html, css } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';

import { authenticatedAPICall } from '../../client/api-client';
import '../../elements/pickup-order/pickup-order';

import {
  Comment,
  ProductionUnit,
  StockCount,
} from '../../types/types';
import { confirmPopup, showNotification } from '../../LanghoutBetonApp';
import { LanghoutCheckbox } from '../../elements/langhout-checkbox/langhout-checkbox';
import { formatDate, formatTime, formatTimeWithoutDate } from '../../util/util';
import {
  findProductionUnit,
  stockCountSuccessURL,
} from '../../util/automatic-stock-count';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

@customElement('langhout-beton-stock-count')
export class LanghoutBetonStockCount extends LitElement {
  static styles = css`
    :host([disableButtons]) {
      pointer-events: none;
      opacity: 0.7;
    }

    .row {
      width: 100%;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }

    .row-margin-small {
      margin-bottom: 10px;
    }

    .row-margin-large {
      margin-bottom: 30px;
    }

    .column {
      padding-left: 10px;
      padding-right: 10px;
      display: flex;
      flex-direction: column;
    }

    langhout-button {
      margin-bottom: 20px;
      max-width: 200px;
      width: 200px;
      margin-left: 15px;
      margin-right: 15px;
      --button-padding: 24px;
    }

    .label {
      text-align: left;
      font-weight: bold;
      width: 200px;
    }

    .value {
      width: 200px;
      text-align: left;
    }

    input {
      padding: 7px;
      width: calc(100% - 18px);
      border-radius: 5px;
      font-size: 16px;
    }

    .fill {
      flex-grow: 1;
    }

    #checkbox-text {
      margin-left: 40px;
    }

    .card {
      border: 1px solid var(--text-color-main);
      padding: 12px;
      margin-bottom: 20px;
      border-radius: 10px;
      position: relative;
    }

    .label {
      color: var(--primary-color);
      min-width: 130px;
      text-align: left;
    }

    .normal-text {
      text-align: left;
      min-width: 150px;
      max-width: 200px;
      padding-right: 15px;
      overflow-wrap: break-word;
    }

    .comment-wrapper {
      margin-top: 10px;
      padding: 10px;
      border: 1px solid var(--text-color-main);
      border-radius: 10px;
    }

    .comment-user-name,
    .comment-time {
      color: var(--primary-color);
      font-weight: bold;
    }

    .comment-row {
      width: 100%;
      display: flex;
      flex-direction: row;
    }
  `;

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  @property({ type: Array })
  private productionUnits: ProductionUnit[] = [];

  @property({ type: Object })
  private lastStockCount: StockCount | null = null;

  @property({ type: Object, reflect: true })
  private selectedProductionUnit: ProductionUnit | null = null;

  private automaticStockCountID: string | null = null;

  private productionUnitID: string | null = null;

  @query('#negative-entry')
  public negativeEntryCheckbox?: LanghoutCheckbox;

  @query('#product-amount')
  public productAmountInput?: HTMLInputElement;

  @query('#comment')
  public commentInput?: HTMLInputElement;

  @query('#playback')
  public audioElement?: HTMLAudioElement;

  constructor() {
    super();
    const params = new URLSearchParams(window.location.search);
    this.automaticStockCountID = params.get('automatic_stock_count_id');
    this.productionUnitID = params.get('production_unit_id');
    this.listProductionUnits();
    document.addEventListener(
      'confirm_different_stock_count_amount',
      this.countStockConfirmEventListener,
    );
    document.addEventListener(
      'confirm_rapid_stock_count',
      this.countStockConfirmEventListener,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      'confirm_different_stock_count_amount',
      this.countStockConfirmEventListener,
    );
    document.removeEventListener(
      'confirm_rapid_stock_count',
      this.countStockConfirmEventListener,
    );
  }

  countStockConfirmEventListener = async () => {
    await this.executeStockCountCall();
  };

  render() {
    return html`
      <audio id="playback">
        <source src="/assets/feedback-stock-count.mp3" type="audio/mpeg" />
      </audio>
      ${this.renderSelectMachine()} ${this.renderSelectProductionUnit()}
    `;
  }

  renderSelectMachine = () => {
    if (this.selectedProductionUnit) {
      return html``;
    }
    return html`
      <h2>Karren lossen</h2>
      <div class="row">
        ${this.productionUnits.map(productionUnit => {
          return html`
            <langhout-button
              text="${productionUnit.name}"
              @click="${(): void => {
                if (productionUnit.setting.product_code === '') {
                  showNotification(
                    `Er is voor ${productionUnit.name} nog geen product ingesteld`,
                  );
                  return;
                }
                this.selectedProductionUnit = productionUnit;
                this.lastCountForProductionUnit();
              }}"
            ></langhout-button>
          `;
        })}
      </div>
      <div class="row">
        <langhout-button
          reverse
          text="Instellingen wijzigen"
          @click="${(): void => {
            window.location.href = '/production-unit-settings';
          }}"
        ></langhout-button>
      </div>
    `;
  };

  renderSelectProductionUnit = () => {
    if (!this.selectedProductionUnit) {
      return html``;
    }
    return html`
      <div class="column">
        <h2>${this.selectedProductionUnit.name}</h2>
        <div class="row row-margin-small">
          <div class="label">Product code:</div>
          <div class="value">
            ${this.selectedProductionUnit.setting.product_code}
          </div>
        </div>
        <div class="row row-margin-small">
          <div class="label">Product:</div>
          <div class="value">
            ${this.selectedProductionUnit.setting.product_description}
          </div>
        </div>
        <div class="row row-margin-large">
          <div class="label">
            <langhout-checkbox
              id="negative-entry"
              @click=${() => {
                if (!this.negativeEntryCheckbox || !this.productAmountInput) {
                  showNotification('Er ging iets fout, probeer opnieuw');
                  return;
                }
                const isChecked = this.negativeEntryCheckbox.checked;

                const val = parseInt(this.productAmountInput.value);
                if (isNaN(val)) {
                  showNotification('Aantal stuks kar is geen correct getal');
                  return;
                }
                if (isChecked && val > 0) {
                  this.productAmountInput.value = (val * -1).toString();
                }

                if (!isChecked && val < 0) {
                  this.productAmountInput.value = (val * -1).toString();
                }
              }}
            ></langhout-checkbox>
            <div id="checkbox-text">Negatieve invoer</div>
          </div>
          <div class="value"></div>
        </div>
        <div class="row row-margin-large">
          <div class="label">Aantal stuks kar:</div>
          <div class="value">
            <input
              type="number"
              inputmode="numeric"
              value="${this.selectedProductionUnit.setting.amount}"
              min="0"
              pattern="[0-9]*"
              id="product-amount"
              name="product-amount"
            />
          </div>
        </div>
        <div class="row row-margin-large">
          <div class="label">Opmerking:</div>
          <div class="value">
            <input type="text" id="comment" name="comment" />
          </div>
        </div>
        <div class="row">
          <langhout-button
            text="Kar lossen"
            @click="${async () => {
              this.disableButtons = true;
              await this.countStock();
              this.disableButtons = false;
            }}"
          ></langhout-button>
        </div>
        <div class="row">
          <langhout-button
            reverse
            text="Annuleren"
            @click="${(): void => {
              this.selectedProductionUnit = null;
              this.lastStockCount = null;
              this.clearAutomaticStockCountContext();
            }}"
          ></langhout-button>
        </div>
        <div class="row">${this.renderLastStockCount()}</div>
      </div>
    `;
  };

  renderLastStockCount = () => {
    if (!this.lastStockCount) {
      return html``;
    }
    return html`
    <div class="column">
      <h1>Laatste telling</h1>
      <div class="card">
        <div class="row wrap-row">
          <div class="label">Machine:</div>
          <div class="normal-text">
            ${this.lastStockCount.production_unit_name}
          </div>
        </div>
        <div class="row wrap-row">
            <div class="label">Los datum:</div>
            <div class="normal-text">
              ${formatDate(this.lastStockCount.time)}
          </div>
        </div>
        <div class="row wrap-row">
          <div class="label">Product code:</div>
          <div class="normal-text">${this.lastStockCount.product_name}</div>
        </div>
        <div class="row wrap-row">
            <div class="label">Los tijd:</div>
            <div class="normal-text">
              ${formatTimeWithoutDate(this.lastStockCount.time)}
            </div>
        </div>
        <div class="row wrap-row">
          <div class="label">Stuks:</div>
          <div class="normal-text">${this.lastStockCount.count}</div>
        </div>
        <div class="row wrap-row">
          <div class="label">Gelost door:</div>
          <div class="normal-text">${this.lastStockCount.created_user}</div>
          <div class="icon-space"></div>
        </div>
      ${this.renderComments(this.lastStockCount)}
      </div>
    </div>
    </div>
  `;
  };

  renderComments = (stockCount: StockCount) => {
    if (!stockCount.comments || stockCount.comments.length === 0) {
      return html``;
    }
    return html`
      ${stockCount.comments.map((comment: Comment) => {
        return html`
          <div class="comment-wrapper">
            <div class="row">
              <div class="comment-user-name">${comment.user_name}</div>
              <div class="fill"></div>
              <div class="comment-time">
                ${formatTime(comment.timestamp, false)}
              </div>
            </div>
            <div class="comment-row">${comment.message}</div>
          </div>
        `;
      })}
    `;
  };

  countStock = async () => {
    if (!this.shadowRoot || !this.selectedProductionUnit) {
      showNotification('something unexpected went wrong, please try again');
      return;
    }

    const amountInput = <HTMLInputElement>(
      this.shadowRoot.querySelector(`#product-amount`)
    );
    const amount = parseInt(amountInput.value);
    if (isNaN(amount) || amount === 0) {
      showNotification(`Stuks per kar moet groter zijn dan 0`);
      return;
    }
    const shouldShowRapidCountWarning = await this.hadRecentStockCount();

    if (this.selectedProductionUnit.setting.amount !== amount) {
      let description = `U heeft het aantal stuks voor product ${this.selectedProductionUnit.setting.product_code} gewijzigd van ${this.selectedProductionUnit.setting.amount} naar ${amount}. Weet u zeker dat dit klopt?`;
      if (shouldShowRapidCountWarning) {
        description =
          'Er is in de laatste 5 minuten nog een kar gelost voor deze machine.<br/>' +
          description;
      }
      confirmPopup({
        confirm_event: 'confirm_different_stock_count_amount',
        title: 'Lossen bevestigen',
        description: description,
      });
      return;
    }
    if (shouldShowRapidCountWarning) {
      confirmPopup({
        confirm_event: 'confirm_rapid_stock_count',
        title: 'Lossen bevestigen',
        description: `Er is in de laatste 5 minuten nog een kar gelost voor deze machine. Weet u zeker dat dit klopt?`,
      });
      return;
    }

    await this.executeStockCountCall();
  };

  executeStockCountCall = async () => {
    if (!this.shadowRoot || !this.selectedProductionUnit) {
      showNotification('Something unexpected went wrong please try again');
      return;
    }
    const amountInput = <HTMLInputElement>(
      this.shadowRoot.querySelector(`#product-amount`)
    );
    const amount = parseInt(amountInput.value);
    if (isNaN(amount) || amount === 0) {
      showNotification(`Stuks per kar moet groter zijn dan 0`);
      return;
    }
    let comment = '';
    if (this.commentInput) {
      comment = this.commentInput.value;
    }
    this.disableButtons = true;
    try {
      const path = `/stock/count`;
      const result = await authenticatedAPICall(
        path,
        'POST',
        new Headers(),
        JSON.stringify({
          production_unit_id: this.selectedProductionUnit.id,
          count: amount,
          product_code: this.selectedProductionUnit.setting.product_code,
          comment: comment,
          automatic_stock_count_id: this.automaticStockCountID,
        }),
      );
      const body = result.body;
      if (result.statusCode !== 200 || !isRecord(body) || !body.id) {
        if (result.statusCode === 200) {
          showNotification('De telling kon niet worden opgeslagen');
        }
        return;
      }

      const successURL = stockCountSuccessURL(this.automaticStockCountID);
      if (successURL) {
        this.clearAutomaticStockCountContext();
      }

      const feedbackFinished = this.playStockCountFeedback();
      showNotification(
        `${amount} stuks geteld voor product ${this.selectedProductionUnit.setting.product_code} op machine ${this.selectedProductionUnit.name}`,
      );
      if (successURL) {
        await feedbackFinished;
        window.location.href = successURL;
        return;
      }
      this.selectedProductionUnit = null;
    } finally {
      this.disableButtons = false;
    }
  };

  private playStockCountFeedback = async (): Promise<void> => {
    const audioElement = this.audioElement;
    if (!audioElement) {
      return;
    }

    await new Promise<void>(resolve => {
      const finish = () => {
        window.clearTimeout(timeoutID);
        audioElement.removeEventListener('ended', finish);
        audioElement.removeEventListener('error', finish);
        resolve();
      };
      const timeoutID = window.setTimeout(finish, 3000);

      audioElement.addEventListener('ended', finish, { once: true });
      audioElement.addEventListener('error', finish, { once: true });
      try {
        audioElement.play().catch(finish);
      } catch {
        finish();
      }
    });
  };

  listProductionUnits = async () => {
    const path = `/production-unit`;
    const result = await authenticatedAPICall(path, 'GET', new Headers());
    if (result.statusCode !== 200) {
      return;
    }
    this.productionUnits = result.body as ProductionUnit[];
    if (!this.productionUnitID) {
      return;
    }

    const productionUnit = findProductionUnit(
      this.productionUnits,
      this.productionUnitID,
    );
    if (!productionUnit) {
      showNotification('De productie-eenheid kon niet worden gevonden');
      this.clearAutomaticStockCountContext();
      return;
    }

    this.selectedProductionUnit = productionUnit;
    await this.lastCountForProductionUnit();
  };

  private clearAutomaticStockCountContext = () => {
    this.automaticStockCountID = null;
    this.productionUnitID = null;
    if (window.location.search) {
      window.history.replaceState({}, '', '/stock-count');
    }
  };

  lastCountForProductionUnit = async () => {
    const path = `/stock/count?order_by=created_at&order_direction=desc&limit=1&offset=0&production_unit_ids=${this.selectedProductionUnit?.id}`;
    const result = await authenticatedAPICall(path, 'GET', new Headers());
    if (Array.isArray(result.body)) {
      this.lastStockCount = (result.body as StockCount[])[0] ?? null;
    }
  };

  hadRecentStockCount = async (): Promise<boolean> => {
    const filterArray: string[] = [];
    if (this.selectedProductionUnit) {
      filterArray.push(`production_unit_ids=${this.selectedProductionUnit.id}`);
      filterArray.push(
        `product_code=${this.selectedProductionUnit?.setting.product_code}`,
      );
    }

    let path = `/stock/count?limit=1&offset=0`;
    const filter = filterArray.join('&');
    if (filter !== '') {
      path += '&' + filter;
    }

    const result = await authenticatedAPICall(path, 'GET', new Headers());
    if (result.statusCode != 200) {
      return false;
    }
    const stockCounts = result.body;
    if (!Array.isArray(stockCounts) || stockCounts.length === 0) {
      return false;
    }
    const stockCount = (stockCounts as StockCount[])[0];

    const dif = new Date().getTime() - new Date(stockCount.time).getTime();
    const difSeconds = dif / 1000;
    return difSeconds < 300;
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-stock-count': LanghoutBetonStockCount;
  }
}
